import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { InvoiceEntity } from './invoice.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';
import { PaymentScheduleOccurrenceAllocationEntity } from './payment-schedule-occurrence-allocation.entity';
import { PaymentScheduleEntity } from './payment-schedule.entity';

/**
 * Occurrence status lifecycle:
 * - planned: PD-15708 — a cycle occurrence with its allocation rows written,
 *   awaiting release (review-then-release, approval threshold, or the
 *   statement lead). Released by a person or by the processor.
 * - pending: Waiting for scheduled date to arrive
 * - released: PD-15708 — a cycle occurrence claimed for charging (by a person
 *   or the processor); the gateway call is in flight
 * - processing: Currently being processed (lock acquired)
 * - retrying: The last attempt failed and a retry is scheduled
 *   (`nextRetryDate` is set, `retryCount` < the schedule's `maxRetries`)
 * - succeeded: Payment captured successfully
 * - failed: TERMINAL — all retry attempts exhausted. Never re-processed
 *   and never regenerated for the same invoice (PD-15187)
 * - skipped: Invoice already paid or other reason to skip
 * - cancelled: Parent schedule was cancelled or paused
 *
 * `retrying` was split out of `failed` under PD-15187: while both states
 * shared one label, the nightly generator could not tell an exhausted
 * occurrence from one awaiting its retry, and regenerated fresh occurrences
 * against invoices that already had one.
 */
export type PaymentScheduleOccurrenceStatus =
	| 'planned'
	| 'pending'
	| 'released'
	| 'processing'
	| 'retrying'
	| 'succeeded'
	| 'failed'
	| 'skipped'
	| 'cancelled';

@Index(
	'payment_schedule_occurrence_status_scheduled_date',
	['status', 'scheduledDate'],
	{},
)
@Index(
	'payment_schedule_occurrence_status_next_retry_date',
	['status', 'nextRetryDate'],
	{},
)
@Index(
	'payment_schedule_occurrence_fk_payment_schedule__id',
	['fkPaymentScheduleId'],
	{},
)
@Index(
	'payment_schedule_occurrence_fk_transaction__id',
	['fkTransactionId'],
	{},
)
/**
 * PD-15187 — one occurrence per schedule, scheduled date and invoice. The
 * generator deduplicates in code, and the unique index
 * `payment_schedule_occurrence_schedule_date_invoice_unique` on
 * (_fk_payment_schedule__id, scheduledDate, _fk_invoice__id) backs it against
 * a second generator run or a race. The index is created by the
 * RelabelRetryingOccurrencesAndAddUniqueIndex migration and deliberately NOT
 * declared here: database sync creates entity indexes unconditionally, and on
 * a tenant that still carries duplicate money-moving occurrences from before
 * the fix (Auvis staging, 2026-09-03) that aborted the tenant's whole sync
 * before it reached the tables that followed. The migration creates the index
 * only when the data is clean and reports the tenant otherwise. Rows with a
 * NULL invoice (recurring and cycle schedules) deduplicate in code.
 */
@Entity(
	'payment_schedule_occurrence',
	{},
	{ label: 'Payment Schedule Occurrence' },
)
export class PaymentScheduleOccurrenceEntity extends BaseEntity {
	@Column('int', {
		name: '_fk_payment_schedule__id',
		unsigned: true,
		comment: 'Parent schedule this occurrence belongs to',
	})
	public fkPaymentScheduleId!: number;

	@Column('int', {
		name: '_fk_invoice__id',
		nullable: true,
		unsigned: true,
		comment: 'Which invoice this occurrence pays (for autopay/installment)',
	})
	public fkInvoiceId!: number | null;

	@Column('int', {
		name: '_fk_transaction__id',
		nullable: true,
		unsigned: true,
		comment: 'Resulting transaction after successful processing',
	})
	public fkTransactionId!: number | null;

	/**
	 * PD-15868 — the companion Account Funds transaction for this occurrence,
	 * when part of the cycle charge was settled from the account balance.
	 * `_fk_transaction__id` records the card/ACH capture; without this second
	 * link the receipt could only guess which funds transaction belonged to
	 * the same charge (previously "same client, same minute").
	 */
	@Column('int', {
		name: '_fk_funds_transaction__id',
		nullable: true,
		unsigned: true,
		comment:
			'Account Funds transaction that settled part of this occurrence, if any',
	})
	public fkFundsTransactionId!: number | null;

	@Column('date', {
		name: 'scheduledDate',
		comment: 'When this payment is scheduled to execute',
	})
	public scheduledDate!: string;

	@Column('datetime', {
		name: 'executedDate',
		nullable: true,
		comment: 'When this payment was actually processed',
	})
	public executedDate!: Date | null;

	@Column('decimal', {
		name: 'amount',
		precision: 13,
		scale: 2,
		comment: 'Target payment amount',
	})
	public amount!: string;

	@Column('decimal', {
		name: 'actualAmount',
		nullable: true,
		precision: 13,
		scale: 2,
		comment:
			'Amount actually charged (may differ if invoice was partially paid manually)',
	})
	public actualAmount!: string | null;

	@Column('varchar', {
		name: 'status',
		length: 32,
		default: () => "'pending'",
		comment: 'Current processing status of this occurrence',
	})
	public status!: PaymentScheduleOccurrenceStatus;

	@Column('smallint', {
		name: 'retryCount',
		default: 0,
		comment: 'Number of retry attempts made so far',
	})
	public retryCount!: number;

	@Column('datetime', {
		name: 'nextRetryDate',
		nullable: true,
		comment: 'When to attempt the next retry (if failed and retries remain)',
	})
	public nextRetryDate!: Date | null;

	@Column('text', {
		name: 'lastError',
		nullable: true,
		comment: 'Error message from the most recent failure',
	})
	public lastError!: string | null;

	@Column('varchar', {
		name: 'lastErrorCode',
		nullable: true,
		length: 64,
		comment: 'Error code from the payment gateway',
	})
	public lastErrorCode!: string | null;

	/**
	 * PD-15708 — a cycle occurrence that must be released by a person before
	 * it charges: review-then-release is on for the tenant, or the planned
	 * amount is above the approval threshold. False = the processor releases
	 * it once the statement lead has elapsed.
	 */
	@Column({
		type: 'boolean',
		name: 'requiresRelease',
		width: 1,
		default: () => "'0'",
		comment: 'Cycle occurrence needs a manual release before charging',
	})
	public requiresRelease!: boolean;

	@Column('datetime', {
		name: 'releasedDate',
		nullable: true,
		comment: 'When a cycle occurrence was released for charging',
	})
	public releasedDate!: Date | null;

	@Column('int', {
		name: '_fk_user__id_released',
		nullable: true,
		unsigned: true,
		comment: 'Who released the cycle occurrence; NULL when the processor did',
	})
	public fkUserIdReleased!: number | null;

	@Column('datetime', {
		name: 'statementSentDate',
		nullable: true,
		comment:
			'When the cycle statement was generated/sent for this occurrence; the charge lead counts from here',
	})
	public statementSentDate!: Date | null;

	@Column('varchar', {
		name: 'skipReason',
		nullable: true,
		length: 64,
		comment:
			'Why a planned cycle occurrence was skipped (e.g. below_minimum_charge)',
	})
	public skipReason!: string | null;

	@OneToMany(
		() => PaymentScheduleOccurrenceAllocationEntity,
		(allocation) => allocation.fkPaymentScheduleOccurrence,
	)
	public allocations!: Array<PaymentScheduleOccurrenceAllocationEntity>;

	// --- Relationships ---

	@ManyToOne(() => PaymentScheduleEntity, (schedule) => schedule.occurrences, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: '_fk_payment_schedule__id', referencedColumnName: 'id' },
	])
	public fkPaymentSchedule!: PaymentScheduleEntity;

	@ManyToOne(() => InvoiceEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;

	@ManyToOne(() => MPaymentTransactionEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_transaction__id', referencedColumnName: 'id' }])
	public fkTransaction!: MPaymentTransactionEntity;
}
